import { useState, useEffect, useMemo } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CircleIcon from '@mui/icons-material/Circle';
import { useGetColors, useGetDeviceModels } from '../../api/requests/device';
import { WEBSOCKET_ADDRESS } from '../../api/requests/urls';
import { useParams } from 'react-router-dom';
import error from '../../sounds/error.wav';
import success from '../../sounds/success.wav';
import useSound from 'use-sound';
import { useGetBoxStatus } from '../../api/requests/inventory';

export default function RegisterInventoryRecordForm({ onClose }) {
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(WEBSOCKET_ADDRESS, {
    onOpen: () => {
      sendJsonMessage({ type: 'AUTH', content: 'Hi' });
    },
    shouldReconnect: () => true,
  });
  const { id: partId } = useParams();
  const [playErrorSound] = useSound(error);
  const [playSuccessSound] = useSound(success);
  const getBoxStatus = useGetBoxStatus();

  const [allModels, setAllModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(1);
  const [started, setStarted] = useState(false);
  const [boxNumber, setBoxNumber] = useState(0);
  const [rows, setRows] = useState(0);
  const [columns, setColumns] = useState(0);
  const [colorId, setColorId] = useState('');
  const [modelId, setModelId] = useState('');
  const [imei, setImei] = useState('');
  const [boxStatus, setBoxStatus] = useState([]);

  useEffect(() => {
    if (started) {
      getBoxStatus(partId, boxNumber).then((status) => {
        setBoxStatus(status);
        setCurrentPosition(Math.max(0, ...status.map((s) => s.position)) + 1);
      });
    }
  }, [started]);

  useEffect(() => {
    if (imei.length === 15) {
      if (currentPosition > rows * columns) {
        playErrorSound();
        return;
      }

      sendJsonMessage({
        type: 'REGISTER_DEVICE',
        content: { imei, boxNumber, partId, modelId, colorId, position: currentPosition },
      });
    }
  }, [imei, sendJsonMessage]);

  useEffect(() => {
    if (lastJsonMessage?.success) {
      setBoxStatus((status) => [...status, { position: currentPosition, imei_1: imei }]);
      setCurrentPosition((p) => p + 1);
      playSuccessSound();
    } else if (lastJsonMessage?.success === false) {
      playErrorSound();
    }
    setImei('');
  }, [lastJsonMessage]);

  function startNextBox() {
    setBoxNumber((num) => Number(num) + 1);
    setStarted(false);
  }

  return (
    <Grid
      container
      direction="column"
      width={{ md: '400px', xs: '250px' }}
      minHeight={{ md: '500px', xs: '300px' }}
      gap={2}
    >
      <ModalHeader
        started={started}
        boxNumber={boxNumber}
        setStarted={setStarted}
        connected={readyState === ReadyState.OPEN}
        model={
          allModels
            .filter((m) => m.tag === modelId)
            .map((m) => `${m.name} (${m.ram} / ${m.storage})`)[0]
        }
        color={colors.find((c) => c.id === colorId)?.name}
      />

      {started ? (
        <>
          <Grid container sx={{ my: 1 }}>
            <Grid item xs={8}>
              <PositionStatus
                rows={Number(rows)}
                cols={Number(columns)}
                completes={boxStatus.map((status) => status.position)}
                currentPosition={currentPosition}
                setCurrentPosition={setCurrentPosition}
              />
            </Grid>
            <Grid item xs={4}>
              <Stack sx={{ px: 2 }}>
                <Typography align="center" fontSize="24px">
                  شماره {currentPosition.toLocaleString('fa-IR')}
                </Typography>
                <Typography fontSize="12px">در انتظار ثبت</Typography>
              </Stack>
            </Grid>
          </Grid>
          <Grid container justifyContent="center">
            <Grid item xs={8}>
              <TextField
                size="small"
                label="IMEI 1"
                type="numeral"
                onChange={(e) => setImei(e.target.value)}
                value={imei}
                InputProps={{
                  endAdornment:
                    imei.length > 0 ? (
                      <IconButton onClick={() => setImei('')} sx={{ p: 0 }}>
                        <HighlightOffIcon position="end" />
                      </IconButton>
                    ) : (
                      <QrCodeScannerIcon position="end" />
                    ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="center">
            <Grid item xs={12}>
              {lastJsonMessage?.success !== undefined && (
                <ResultBox
                  success={lastJsonMessage?.success}
                  imei={lastJsonMessage?.imei1}
                  position={lastJsonMessage?.position}
                />
              )}
            </Grid>
          </Grid>
          <Grid container flexGrow={1} alignItems="end" justifyContent="end" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              اتمام
            </Button>
            <Button variant="contained" onClick={startNextBox}>
              شروع جعبه‌ی بعدی
            </Button>
          </Grid>
        </>
      ) : (
        <RegistrationInfo
          started={started}
          boxNumber={boxNumber}
          setStarted={setStarted}
          setColorId={setColorId}
          setBoxNumber={setBoxNumber}
          setColumns={setColumns}
          setRows={setRows}
          setModelId={setModelId}
          rows={rows}
          columns={columns}
          colorId={colorId}
          modelId={modelId}
          allModels={allModels}
          setAllModels={setAllModels}
          colors={colors}
          setColors={setColors}
        />
      )}
    </Grid>
  );
}

function PositionStatus({ cols, rows, completes, currentPosition, setCurrentPosition }) {
  function isCompleted(i, j) {
    return completes.includes(getPosition(i, j));
  }
  function getPosition(i, j) {
    return i * cols + j + 1;
  }

  return (
    <Stack direction="column" gap={1}>
      {[...Array(rows).keys()].map((row, i) => (
        <Grid container key={i} gap={1}>
          {[...Array(cols).keys()].map((col, j) => (
            <Grid key={j} sx={{ flex: 1 }}>
              <Grid>
                <Tooltip title={isCompleted(i, j) ? 'ثبت‌شده' : 'ثبت‌نشده'}>
                  <Box
                    sx={{
                      backgroundColor: isCompleted(i, j) ? 'primary.main' : 'secondary.main',
                      height: '24px',
                      animation:
                        getPosition(i, j) === currentPosition ? 'blink 1s infinite' : 'none',
                      cursor: 'pointer',
                      borderRadius: 1,
                    }}
                    onClick={() => setCurrentPosition(getPosition(i, j))}
                  >
                    <Typography
                      align="center"
                      fontSize="small"
                      color={isCompleted(i, j) ? 'white' : 'black'}
                      lineHeight="24px"
                    >
                      {getPosition(i, j).toLocaleString('fa-IR')}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          ))}
        </Grid>
      ))}
    </Stack>
  );
}

function ModalHeader({ started, color, model, boxNumber, setStarted, connected }) {
  return started ? (
    <Grid container>
      <Grid item alignSelf="end" sx={{ pr: 2 }}>
        <CircleIcon color={connected ? 'success' : 'error'} fontSize="small" />
      </Grid>
      <Grid item xs={8} alignSelf="center">
        <Typography fontSize="22px" fontWeight={500}>
          جعبه شماره {Number(boxNumber).toLocaleString('fa-IR')} (مهیمن)
        </Typography>
      </Grid>
      <Grid item xs={6} alignSelf="center">
        <Typography fontSize="14px" color="text.secondary">
          {model}
        </Typography>
      </Grid>
      <Grid item xs={3} alignSelf="center">
        <Typography fontSize="14px" color="text.secondary">
          {color}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Button
          size="small"
          endIcon={<EditIcon sx={{ fontSize: '12px' }} />}
          onClick={() => setStarted(false)}
        >
          تغییر
        </Button>
      </Grid>
    </Grid>
  ) : (
    <Grid container>
      <Grid item xs={8} alignSelf="center">
        <Typography fontSize="22px" fontWeight={500}>
          ثبت دستگاه جدید در انبار
        </Typography>
      </Grid>
    </Grid>
  );
}

function RegistrationInfo({
  boxNumber,
  setStarted,
  setBoxNumber,
  setColorId,
  setModelId,
  setRows,
  setColumns,
  colorId,
  modelId,
  rows,
  columns,
  allModels,
  setAllModels,
  colors,
  setColors,
}) {
  const getDeviceModels = useGetDeviceModels();
  const getColors = useGetColors();

  useEffect(() => {
    getDeviceModels().then((d) => setAllModels(d));
    getColors().then((c) => setColors(c));
  }, []);

  const isStartButtonDisabled = useMemo(
    () =>
      !(columns !== '0' && rows !== '0' && modelId !== '' && colorId !== '' && boxNumber !== '0'),
    [boxNumber, rows, columns, colorId, modelId]
  );

  return (
    <Stack direction="column" justifyContent="space-between" flexGrow={1}>
      <Grid container justifyContent="space-between" spacing={1}>
        <Grid item xs={4}>
          <TextField
            size="small"
            label="شماره جعبه"
            type="number"
            value={boxNumber}
            onChange={(e) => setBoxNumber(e.target.value)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            size="small"
            label="# سطر"
            type="number"
            value={rows}
            onChange={(e) => setRows(e.target.value)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            size="small"
            label="# ستون"
            type="number"
            value={columns}
            onChange={(e) => setColumns(e.target.value)}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            select
            size="small"
            fullWidth
            label="مدل"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
          >
            {allModels?.map?.((m) => (
              <MenuItem value={m.tag} key={m.tag}>{`${m.name} (${m.ram} / ${m.storage})`}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField
            select
            size="small"
            fullWidth
            label="رنگ"
            value={colorId}
            onChange={(e) => setColorId(e.target.value)}
          >
            {colors?.map?.((color) => (
              <MenuItem value={color.id} key={color.id}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexGrow={1}
                >
                  <Typography>{color.name}</Typography>
                  <CircleIcon fontSize="small" sx={{ color: color.code }} />
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <Grid container justifyContent="space-between" spacing={1}>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setStarted(true)}
            disabled={isStartButtonDisabled}
          >
            انصراف
          </Button>
        </Grid>

        <Grid item xs={6}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              const r = Math.min(columns, rows);
              const c = Math.max(columns, rows);
              setColumns(c);
              setRows(r);
              setStarted(true);
            }}
            disabled={isStartButtonDisabled}
          >
            شروع
          </Button>
        </Grid>
      </Grid>
    </Stack>
  );
}

function ResultBox({ success, imei, position }) {
  return (
    <Alert
      severity={success ? 'success' : 'error'}
      variant="outlined"
      sx={{
        '.MuiAlert-message': {
          flexGrow: 1,
        },
      }}
    >
      <AlertTitle>{success ? 'ثبت شد' : 'خطا'}</AlertTitle>

      {success !== null &&
        (success ? (
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography textAlign="right">IMEI: {imei}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>موقعیت: {position}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={8}>
              <Typography>IMEI: {imei}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>موقعیت: {position}</Typography>
            </Grid>
            <Divider orientation="vertical" flexItem />
          </Grid>
        ))}
    </Alert>
  );
}
